import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Col, Collapse, Input, Label, Row, Spinner, Table, Toast, ToastBody, ToastHeader } from 'reactstrap';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { useAppDispatch, useAppSelector } from '../hooks/index';
import { AfrReview, ReviewsRequestFilter, selectAfrReviewLoadingStatus } from '../redux/AfrReviewSlice';
import { loadAfr, resetState } from '../redux/AfrSlice';
import { resetConceptState } from "../redux/ConceptSlice";
import { TaskLogStarter, generateTaskLog } from '../redux/DocumentsSlice';
import { EditEntityQuery, loadEntity } from '../redux/EntitySlice';
import { AssignmentEvent, loadEvents } from '../redux/EventsSlice';
import { TaxonomyTypeEnum } from '../redux/TaxonomyVersionSlice';
import dateHelper from '../utils/dateHelper';
import utilityHelper from '../utils/utilityHelper';


interface DataRow {
    entityid: string;
    entityname: string;
    entitytype: string;
    fiscalyear: number;
    typeInfo: string | null;
    afrReceivedDate: string | null;
    auditReceivedDate: string | null;
    afrVerifiedDate: string | null;
    assignedto: string;
    assigmentdate: string;
    daystoassign: number | null;
    daystoverify: number | null;
    connector: string;
    readyForReview: string;
}

const AFRAssignmentMaster = (props: { filter: ReviewsRequestFilter, taxonomyType: TaxonomyTypeEnum; filteredReviewList: Array<AfrReview> }) => {
    const dispatch = useAppDispatch();
    const tabulatorRef = React.useRef<any>(null);

    const [currentFilter, setCurrentFilter] = useState("");
    const [entityAFRInfo, setEntityAFRInfo] = useState("");
    const [dataKey, setDataKey] = useState("");

    const [dataTable, setDataTable] = React.useState<Array<DataRow>>([]);

    const [tableSortValue, setTableSortValue] = React.useState<any>([]);

    const [isLoadingData, setIsLoadingData] = React.useState<boolean>(false);


    const loadingStatus: 'idle' | 'loading' | 'failed' | 'uninitialized' = useAppSelector(selectAfrReviewLoadingStatus);

    async function startTaskLog() {
       
        const taskLogData: TaskLogStarter = {
            taskLogFilter: props.filter
        };
        const response = await dispatch(generateTaskLog(taskLogData));
        const doc = unwrapResult(response);
        if (doc != null) {

            //TODO make this a generic function in document slice
            //that takes mime type, base64 encoded string, filename
            //and returns a blob.
            //TODO expand to handle xhtml and excel files
            var url = "data:" + doc.mimeType + ";base64," + doc.content;
            await fetch(url)
                .then(res => res.blob())
                .then(otho => {

                    const data = window.URL.createObjectURL(otho);
                    var link = document.createElement("a");
                    link.href = data;
                    link.download = "TaskLog.xlsx"; //"report.xhtml";
                    link.click();
                    setTimeout(function () {
                        // For Firefox it is necessary to delay revoking the ObjectURL
                        window.URL.revokeObjectURL(data);
                    }, 100);
                });
        }

        return doc;
        
    }

    async function startGeneratingTaskLog() {
        try {
            await startTaskLog();
        } catch (err) {
            console.log(err);
        }
    }

    /* Adding Columns to grid */
    const defaultColumns = [
        { title: 'Entity ID', field: 'entityid' },
        {
            title: 'Entity Name', field: 'entityname',
            formatter: function (cell: any, formatterParams: any, onRendered: any) {
                return utilityHelper.SnakeCaseToTitleCase(cell.getValue());
            },
        },
        { title: 'Entity Type', field: 'entitytype' },
        { title: 'Fiscal Year', field: 'fiscalyear' },
        { title: 'Report Type', field: 'typeInfo' },
        { title: 'AFR Received', field: 'afrReceivedDate', sorter: "date", sorterParams: {
            format: "LL/dd/yyyy",
            alignEmptyValues: "bottom",
        } },
        {
            title: 'Audit/DEW Received', field: 'auditReceivedDate', sorter: "date", sorterParams: {
                format: "LL/dd/yyyy",
                alignEmptyValues: "bottom",
            }
        },
        {
            title: 'Verified', field: 'afrVerifiedDate', sorter: "date", sorterParams: {
                format: "LL/dd/yyyy",
                alignEmptyValues: "bottom",
            }
        },
        { title: 'Assigned to', field: 'assignedto' },
        {
            title: 'Assignment Date', field: 'assigmentdate', sorter: "date", sorterParams: {
                format: "LL/dd/yyyy",
                alignEmptyValues: "bottom",
            }
        },
        { title: 'Days to Assign', field: 'daystoassign' },
        { title: 'Days to Verify', field: 'daystoverify' },

    ];

    const newGridOptions = {
        layout: "fitDataStretch",
        layoutColumnsOnNewData: true,
        dataTree: false,
        selectable: 1,
        selectableRollingSelection: false,
        height: "350",  // to be adjusted if another thing is decided
        // columnMaxWidth: 120,
        index: "entityid",
        editable: (cell: any) => handleEditCheck(cell),
        rowClick: (e: any, row: any) => handleRowClick(e, row),
    }

    // **********************************************
    // Returns the first search criteria with data
    // **********************************************
    const getMissingDataFilter = function () {
        var mdf : string = "MISSING_AUDIT_DOCUMENT";
        switch( props.taxonomyType ) {
            case TaxonomyTypeEnum.COURT_REPORTING_939:
                mdf = "NOT_SUBMITTED";
                break;
        }
        return mdf;
    }
    const [missingDataFilter,setMissingDataFilter] = useState(getMissingDataFilter())
    const getMissingDataLabel = function () {
        var mdf : string = "Missing Audit Document";
        switch( props.taxonomyType ) {
            case TaxonomyTypeEnum.COURT_REPORTING_939:
                mdf = "Not Submitted";
                break;
        }
        return mdf;
    }
    const [missingDataLabel, setMissingDataLabel] = useState(getMissingDataLabel());

    /*******************************************************************************
     * Fill data for the grid
     *******************************************************************************/


    /*******************************************************************************/


    /* *****************************************************************************
     * Events and Effects
     *******************************************************************************/
    function isDetailDirty() {
        return false;

    }

    function handleRowClick(e: any, row: any) {
        //e - the click event object
        //row - row component
        if (row._row.type != "row") { return }

        row.getTable().deselectRow();
        row.select();
        var entityID = row.getData().entityid;
        var AFRId = row.getData().connector; //entityID + "_" + row.getData().fiscalyear;
        var entityinfo = " - " + entityID + " - " + row.getData().entityname + ", Fiscal Year: " + row.getData().fiscalyear;
        setEntityAFRInfo(entityinfo);
        setDataKey(entityID);

        dispatch(loadAfr(AFRId));
        var eQuery: EditEntityQuery = {
            entityId: entityID,
            taxonomyYear: -1
        };
        dispatch(loadEntity(eQuery));
        dispatch(loadEvents(AFRId));
        dispatch(resetConceptState({}));

    }

    // special logic to verify if a cell is editable or not
    // editable    --> return true
    // no editable --> return false
    function handleEditCheck(cell: any) {
        if (isDetailDirty()) {
            return false;
        }

        return true;
    }

    // Generate export based on the search criteria being used
    const handleEventExportClick = function () {
        //alert("Develop is pending!");
        startGeneratingTaskLog();
    }

    const handleChange = function (event: any) {

        var filter: string = event.target.value
        dispatch(resetState({}));
        setCurrentFilter(filter);
        setDataKey("");

    }


    React.useEffect(function () {
        if (currentFilter == "") {
            setMissingDataFilter(getMissingDataFilter());
        }        
    }, [ props.filter]);

    const initTableData = () => {

        if (props.filteredReviewList && props.filteredReviewList.length > 0) {

            var AFRReviewList = props.filteredReviewList.filter(r => r.readyForReview === currentFilter || currentFilter == "");
            var result: Array<DataRow> = [];

            for (var rev of AFRReviewList) {
                var lastAssigned: AssignmentEvent | null = null;
                var assignedto = "";
                var assigmentdate = "";
                var afrReceivedDateValue = "";
                var auditReceivedDateValue = "";
                var afrVerifiedDateValue = "";

                if (rev.assignments && rev.assignments.length > 0) {
                    var arrayForSort = [...rev.assignments];
                    arrayForSort.sort((a, b) => (a.timestamp < b.timestamp) ? 1 : ((b.timestamp < a.timestamp) ? -1 : 0));

                    lastAssigned = arrayForSort[0];
                    if (lastAssigned.assignee) {
                        assignedto = lastAssigned.assignee.lastName;
                        assigmentdate = dateHelper.stringDateInFormat(lastAssigned.timestamp, "LL/dd/yyyy");
                    }
                }

                if (rev.afrReceivedDate != null) {
                    afrReceivedDateValue = dateHelper.stringDateInFormat(rev.afrReceivedDate, "LL/dd/yyyy");
                }

                if (rev.auditReceivedDate != null) {
                    auditReceivedDateValue = dateHelper.stringDateInFormat(rev.auditReceivedDate, "LL/dd/yyyy");
                }

                if (rev.verifiedDate != null) {
                    afrVerifiedDateValue = dateHelper.stringDateInFormat(rev.verifiedDate, "LL/dd/yyyy");
                }

                var row = {
                    entityid: rev.connector.split(",")[0],
                    entityname: utilityHelper.toProperCase(rev.entityName),
                    entitytype: utilityHelper.toProperCase(rev.entityType.replace("_", " ")),
                    fiscalyear: rev.afrYear,
                    typeInfo: rev.afrTypeInfo,
                    assignedto: assignedto,
                    assigmentdate: assigmentdate,
                    daystoassign: (rev.daysToAssign < 0) ? null : rev.daysToAssign,
                    daystoverify: (rev.daysToVerify < 0) ? null : rev.daysToVerify,
                    afrReceivedDate: afrReceivedDateValue,
                    auditReceivedDate: auditReceivedDateValue,
                    afrVerifiedDate: afrVerifiedDateValue,
                    connector: rev.connector,
                    readyForReview: rev.readyForReview
                }

                result.push(row);
            }
            setDataTable(result);
        } else {
            setDataTable([]); // Clear the data table if no data is available
        }       
        setIsLoadingData(false);
    };

    React.useEffect(() => {

        initTableData();

    }, [currentFilter, props.filteredReviewList])

    React.useEffect(() => {

        const table = new Tabulator("#table", {
            data: dataTable,
            columns: defaultColumns,
            layout: "fitColumns",
            layoutColumnsOnNewData: true,
            dataTree: false,
            selectable: 1,
            selectableRollingSelection: false,
            height: "350",
            index: "entityid",
            editable: handleEditCheck,
            initialSort: tableSortValue,
            placeholder: "There is no data for this search criteria. Please try another option!"
        });

        // subscribe multiple tabulator events
        table.on("rowClick", handleRowClick);

        table.on('dataSorted', (sorters:any) => {
            setTableSortValue(sorters);
        });

        

    }, [dataTable]);

    React.useEffect(() => {

        setCurrentFilter("");
        setCurrentFilter("");
        setEntityAFRInfo("");
        setDataKey("");
        setDataTable([]);
        setTableSortValue([]);

    }, [])

    React.useEffect(() => {
        if (loadingStatus == 'loading') {
            setIsLoadingData(true);
        }
    }, [loadingStatus]);


    /*******************************************************************************/
    return (
        <React.Fragment>
            <div>
                <Toast className="auditDetail">
                    <ToastHeader className="auditDetail">Assignment {entityAFRInfo}</ToastHeader>
                    <ToastBody className="auditDetail">
                        <Row>
                            <Col>
                                <Collapse isOpen={isLoadingData == true}>
                                    <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                                        type="grow" color="warning"
                                        hidden={isLoadingData == false} />
                                </Collapse>
                                <Collapse isOpen={isLoadingData == false}>
                                <Table borderless>
                                    <thead>
                                        <tr>
                                            <th className="card-header" style={{
                                                color: "white",
                                                backgroundColor: "#458BCA",
                                                borderRadius: "0px",
                                                fontWeight: "initial"
                                            }}>Ready for Review?
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th>                                      
                                                <Label className="ml-4" check>
                                                    <Input type="radio"
                                                        name="radioFilterReady"
                                                        value={"READY"}
                                                        onChange={handleChange}
                                                        checked={(currentFilter === "READY")}
                                                        disabled={isLoadingData == true}
                                                    />
                                                    <b>Yes</b>
                                                </Label>                                               
                                            </th>
                                            <th>
                                                <Label className="ml-4" check>
                                                    <Input type="radio"
                                                        name="radioFilterReturned"
                                                        value={"RETURNED"}
                                                        onChange={handleChange}
                                                        checked={(currentFilter === "RETURNED")}
                                                        disabled={isLoadingData == true}
                                                    />
                                                    <b>No</b>
                                                </Label>
                                            </th>
                                            <th >
                                                <Label className="ml-4" check>
                                                    <Input type="radio"
                                                        name="radioFilterMissing"
                                                        value={missingDataFilter}
                                                        onChange={handleChange}
                                                        checked={(currentFilter === missingDataFilter)}
                                                        disabled={isLoadingData == true}
                                                    />
                                                    <b>No ({missingDataLabel})</b>
                                                </Label>
                                            </th>
                                            <th>
                                                <Label className="ml-4" check>
                                                    <Input type="radio"
                                                        value={"VERIFIED"}
                                                        name="radioFilterVerified"
                                                        onChange={handleChange}
                                                        checked={(currentFilter === "VERIFIED")}
                                                        disabled={isLoadingData == true}
                                                    />
                                                    <b>Verified</b>
                                                </Label>
                                            </th>
                                        </tr>
                                    </tbody>
                                    </Table>
                                </Collapse>
                            </Col>
                            <Col md="1">
                                <Button type="button" name="export" onClick={handleEventExportClick} disabled={ isLoadingData == true }>Export</Button>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div id="table" ref={tabulatorRef} hidden={ isLoadingData == true } />
                            </Col>
                        </Row>

                    </ToastBody>
                </Toast>


            </div>

        </React.Fragment>
    );
};

export default AFRAssignmentMaster;